import React, {useEffect, useState, useCallback} from 'react';
import {SVGS} from '../../../../assets/images';
import {useAppDispatch, useAppSelector} from '../../../../hooks/hooks';
import {deleteAbsenceType, getAbsencesTypes, upsertAbsenceTypeLocally} from '../../../../features/types';
import {TiminatorAbsenceType} from '../../../../features/types/types';
import IconTextLarge from '../../../../components/shared/large-icon-text';
import PageWrapperV2 from '../../../../components/shared/page-wrapper-v2';
import PageHeader from '../../../../components/shared/page-header';
import { useLocation, useNavigate } from 'react-router-dom';
import PageBody from '../../../../components/shared/page-body';
import { useIntl } from 'react-intl';
import CreateNewButtonV2 from '../../../../components/shared/create-new-button-v2';
import TiminatorTableV2 from '../../../../components/shared/table-v2';
import { DeleteDialog } from '../../../../components/shared/delete-dialog';
import { Lx } from '../../../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { useMediaQuery } from 'react-responsive';
import { DeleteIconButton } from '../../../../components/shared/delete-icon-button';
import { useAbsenceTypesTable } from './hook/useAbsencesTypesTable';
import { TypeFilter, DEFAULT_TYPE_FILTER_STATE } from '../type-filter';
import PageFooter from '../../../../components/shared/page-footer';
import { TypeHeader } from '../type-header';
import { useInfinitePagination } from '../../../../hooks/useInfinitePagination';
import { canManageAbsenceTypes, canSeeAbsenceTypesSection } from '../../../../features/profile/entityPermissions';
import { getVisibleTypeTabs } from '../../tabs';

export default function AbsencesTypes() {
    const dispatch = useAppDispatch();
    const absencesTypes = useAppSelector(store => store.types.absences)
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationState = location.state as { createdAbsenceType?: TiminatorAbsenceType } | null;

    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [search, setSearch] = useState('');
    const isMobile = useMediaQuery({ maxWidth: 1000  });

    const hasMore = absencesTypes.items.length < absencesTypes.total;
    const typesState = useAppSelector(store => store.types);
    const profile = useAppSelector(store => store.profile);
    const canEditAbsenceTypes = canManageAbsenceTypes(profile);
    const canViewAbsenceTypes = canSeeAbsenceTypesSection(profile, absencesTypes.permissions);

    const fetchAbsenceTypesPage = useCallback((page: number) => {
        return dispatch(getAbsencesTypes({ page, force: true })).unwrap();
    }, [dispatch]);

    const { loadInitial, loadNext, isLoadingMore, isLoadingInitial, reset } = useInfinitePagination({
        hasMore,
        fetchPage: fetchAbsenceTypesPage,
    });

    const [mobileView, setMobileView] = useState<'list' | 'filter'>('list');

    const handleMobileFilterClick = () => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    };

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    const handleMobileHeaderClose = useCallback(() => {
        handleFilterChange(DEFAULT_TYPE_FILTER_STATE);
    }, [handleFilterChange]);

    const [open, setOpen] = useState(false);
    const [selectedAbsenceType, setSelectedAbsenceType] = useState<TiminatorAbsenceType | null>(null);

    useEffect(() => {
        if (!canViewAbsenceTypes) {
            const availableTab = getVisibleTypeTabs(profile, {
                project: typesState.projects.permissions,
                shift: typesState.shifts.permissions,
                absence: absencesTypes.permissions,
                tag: typesState.tags.permissions,
            })[0];

            if (availableTab) {
                navigate(availableTab.path, { replace: true });
            }
        }
    }, [absencesTypes.permissions, canViewAbsenceTypes, navigate, profile, typesState.projects.permissions, typesState.shifts.permissions, typesState.tags.permissions]);

    useEffect(() => {
        if (!canViewAbsenceTypes) {
            return;
        }

        loadInitial()
            .then(() => {
                if (navigationState?.createdAbsenceType) {
                    dispatch(upsertAbsenceTypeLocally(navigationState.createdAbsenceType));
                    navigate(location.pathname, { replace: true });
                }
            })
            .catch(() => { /* handled elsewhere */ });
    }, [canViewAbsenceTypes, dispatch, loadInitial, location.pathname, navigate, navigationState]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const { columns, data, isEmpty } = useAbsenceTypesTable(
        search,
        selectedStatus,
        isMobile
    );
    const showEmptyState = !isLoadingInitial && isEmpty;

    if (!canViewAbsenceTypes) {
        return null;
    }

    const enhancedColumns = columns
        .filter(col => canEditAbsenceTypes || col.id !== 'deleteButton')
        .map(col => {
            if (col.id === 'deleteButton') {
                return {
                    ...col,
                    format: (rowData: TiminatorAbsenceType) => (
                        <DeleteIconButton
                            onClick={() => {
                                setSelectedAbsenceType(rowData);
                                setOpen(true);
                            }}
                        />
                    ),
                };
            }
            return col;
        });
    
    function onRowEdit(absenceType: TiminatorAbsenceType) {
        if (!canEditAbsenceTypes) {
            return;
        }

        navigate(NAVIGATION_ROUTES.EDIT_ABSENCE_TYPE, {
            state: { absenceType }
        });
    }
    
    const handleDeleteCancel = () => {
        setOpen(false);
        setSelectedAbsenceType(null);
    };
    
    const handleDeleteConfirm = () => {
        if (selectedAbsenceType) {
            dispatch(deleteAbsenceType(selectedAbsenceType.id)).unwrap().then(() => {
                reset();
                loadInitial().catch(() => { /* handled elsewhere */ });
            });
        }
        handleDeleteCancel();
    };

    const handleCreateNew = () => {
        if (!canEditAbsenceTypes) {
            return;
        }

        navigate(NAVIGATION_ROUTES.CREATE_ABSENCE_TYPE);
    }
    return <>
        <PageWrapperV2>
            <PageHeader>
                <TypeHeader
                    isEmpty={showEmptyState}
                    isFilterOpen={mobileView === 'filter'}
                    onMobileFilterClick={handleMobileFilterClick}
                    onCreateNew={handleCreateNew}
                    canCreate={canEditAbsenceTypes}
                    onMobileFilterClose={handleMobileHeaderClose}
                    loading={isLoadingInitial}
                />
            </PageHeader>
            <PageBody justifyContent={showEmptyState ? 'center' : 'flex-start'} alignItems='stretch'>
                {   
                    !showEmptyState
                        ? (
                            <TypeFilter
                                search={search}
                                selectedStatus={selectedStatus}
                                isMobile={isMobile}
                                mobileView={mobileView}
                                onFilterChange={handleFilterChange}
                                onMobileClose={handleMobileFilterClose}
                            >
                                <TiminatorTableV2 
                                    onRowClick={canEditAbsenceTypes ? onRowEdit : undefined} 
                                    columns={enhancedColumns}
                                    data={data}
                                    hasMore={hasMore}
                                    onLoadMore={loadNext}
                                    isLoadingMore={isLoadingMore} />
                            </TypeFilter>
                        )
                        : <IconTextLarge 
                            text={intl.formatMessage({id: Lx.Type.NO_ABSENCE_TYPES})} 
                            subtext={intl.formatMessage({id: Lx.Type.CREATE_AND_USE_ABSENCE_TYPE})} 
                            icon={<SVGS.TypesIcon />} />
                }
            </PageBody>
            {
                isMobile && mobileView === 'list' && canEditAbsenceTypes &&
                    <PageFooter>
                        <CreateNewButtonV2 onClick={handleCreateNew} />
                    </PageFooter>
            }
        </PageWrapperV2>
        <DeleteDialog 
            open={open} 
            onCancel={handleDeleteCancel} 
            onConfirmDelete={handleDeleteConfirm}
            deleteHeader={intl.formatMessage({id: Lx.Type.DELETE_ABSENCE_TYPE})}
            deleteMessage={intl.formatMessage({id: Lx.Type.DELETE_ABSENCE_TYPE_MESSAGE}, { name: selectedAbsenceType?.name })}
            title={intl.formatMessage({id: Lx.General.ABSENCE_TYPE})} />
    </>
}
